// Lane B2's screens, registered the way every lane registers its own (lane contract C-29) and hosted in MissionShell's
// content slot, inside its one `.mc` island (none of them draws a McRoot); their V1 parts (record after the fact, the
// not-found screen, a read's refusal) render through the shell's V1 slot, outside the island (shell/island.tsx):
//
//   #requests                     the Requests list            gates request.list_mine · queue_hrbp · queue_ta · list
//   #requests/new                 the Hiring Request, new      only where `request.create` is callable ("Create request:
//                                                              HRBP Yes; all others No."), else the not-found screen
//   #requests/RQ-####             the Hiring Request           behind the request's own read (its refusal is §3.3's)
//   #requisitions/assignment      the TA Manager's assignment queue (`#requisitions/queue`, V1's TA queue, lands here too)
//                                 gate recruiter.queue, refused here before the page draws
//   #requisitions/R-####          the requisition detail, and its `/timeline` tab; a tab another lane registers through
//                                 registerRequisitionTab (RequisitionDetail.tsx) adds its own route
//   #compensation                 Compensation's queue         gate comp_review.queue
//   #compensation/RQ-####         the Compensation worksheet
//   #requisitions                 the Requisitions list (D-11), the bare route with its query string (`?view=board`,
//                                 the filters); gate requisition.list. Every sub-route above keeps its own pattern.
//
// A record route loads behind the design system's skeleton at the screen's final geometry (DC-25), never V1's: the
// request's guard draws the Hiring Request's own loading state until its read settles, then hands the refusal screens
// to ObjectReadGuard.
// The bare `#requisitions` list is the Mission Control list (views/requisition/RequisitionsList.tsx), so the registry wins
// over V1's Requisitions.tsx there; its C-B10-13 slots are in views/requisition/slots.ts and lane B10 registers their
// occupants from its own views/b10/lists/register.ts. App.tsx's former B2 cases and its Compensation nav row are removed
// by the integration hunk in the lane report; the nav row is lit from `shell_vocab` by the backend
// (app/tools/comp_review.py).

import { createElement, type ReactNode } from "react";
import { dispositionOpenWorklist, requestOpen } from "@/api/tools";
import { isClientRefusal, useRead } from "@/api/useTool";
import { useSession } from "@/session/useSession";
import { V1Slot } from "@/shell/island";
import { registerView, type ViewProps } from "@/shell/viewRegistry";
import { ObjectReadGuard } from "@/ui";
import { RecordAfterFact } from "@/ui/RecordAfterFact";
import { NotFound } from "@/views/NotFound";
import { AssignmentQueue } from "@/views/assignment/AssignmentQueue";
import { CompensationQueue } from "@/views/compensation/CompensationQueue";
import { CompReview } from "@/views/compensation/CompReview";
import { HiringRequest, RequestLoading } from "@/views/request/HiringRequest";
import { RequestsList } from "@/views/request/RequestsList";
import { RequisitionRoute } from "@/views/requisition/RequisitionDetail";
import { RequisitionsList } from "@/views/requisition/RequisitionsList";

const RQ = /^RQ-\d+$/;

/** A request's record routes open behind its own read: the mc skeleton while it is out, then a refusal is the canonical
 * not-found or no-access screen. */
function RequestGuard({ id, children }: { id: string; children?: ReactNode }) {
  const q = useRead(["request", id], () => requestOpen({ request_id: id }));
  if (q.isPending) return createElement(RequestLoading);
  return createElement(ObjectReadGuard, { query: q }, children);
}

function RequestsRoute({ route }: ViewProps) {
  const { can } = useSession();
  const [first, ...rest] = route.parts;
  if (first === undefined) return createElement(RequestsList);
  if (rest.length) return createElement(NotFound, { route: route.raw });
  if (first === "new") return can("request.create") ? createElement(HiringRequest) : createElement(NotFound, { route: route.raw });
  if (!RQ.test(first)) return createElement(NotFound, { route: route.raw });
  return createElement(
    RequestGuard,
    { id: first },
    createElement(V1Slot, { key: route.raw, children: createElement(RecordAfterFact, { id: first, family: "request" }) }),
    createElement(HiringRequest, { key: first, id: first }),
  );
}

function CompensationRoute({ route }: ViewProps) {
  const [first, ...rest] = route.parts;
  if (first === undefined) return createElement(CompensationQueue);
  if (rest.length || !RQ.test(first)) return createElement(NotFound, { route: route.raw });
  return createElement(CompReview, { key: first, requestId: first });
}

registerView("requests", RequestsRoute, {
  lane: "B2", gates: ["request.list_mine", "request.queue_hrbp", "request.queue_ta", "request.list"],
});
// A sub-route of `#requisitions` is not gated by App.tsx's landing rule, so the route refuses on its own registered gate
// before the page draws (F-56): a seat without `recruiter.queue` gets the not-found screen, never the page over a refusal.
registerView(/^requisitions\/(?:queue|assignment)$/, function AssignmentRoute({ route }: ViewProps) {
  const { can } = useSession();
  return can("recruiter.queue") ? createElement(AssignmentQueue) : createElement(NotFound, { route: route.raw });
}, { lane: "B2", gates: ["recruiter.queue"] });
registerView(/^requisitions\/R-\d+(?:\/timeline)?$/, RequisitionRoute, { lane: "B2", specificity: 1 });
// F-185: the requisition's Plan and Debriefs tabs, and its Offer tab behind the worklist's own read (a refused read is
// the canonical not-found or no-access screen, as V1's section gave it, never the record's header over a refusal)
registerView(/^requisitions\/R-\d+\/(?:plan|debriefs)$/, RequisitionRoute, { lane: "B2", specificity: 1 });
registerView(/^requisitions\/R-\d+\/offer$/, function OfferTabRoute(props: ViewProps) {
  const id = props.route.parts[0] ?? "";
  const q = useRead(["worklist", id], () => dispositionOpenWorklist({ requisition_id: id }));
  if (q.error && isClientRefusal(q.error)) return createElement(ObjectReadGuard, { query: q });
  return createElement(RequisitionRoute, props);
}, { lane: "B2", specificity: 1 });
registerView("compensation", CompensationRoute, { lane: "B2", gates: ["comp_review.queue"] });

// The Requisitions list (D-11): the bare route, its query string included (a pattern is tested against the path alone).
registerView(/^requisitions$/, RequisitionsList, { lane: "B2", gates: ["requisition.list"] });

